import { useEffect, useId, useRef } from "react";
import { X } from "lucide-react";

export function Modal({ open, onClose, title, children, footer, size = "md" }) {
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const returnFocusTo = document.activeElement;
    const focusableSelector = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusInitial = window.requestAnimationFrame(() => {
      const first = dialogRef.current?.querySelector("[data-autofocus], " + focusableSelector);
      (first || dialogRef.current)?.focus();
    });
    const onKeyDown = (event) => {
      if (event.key === "Escape") { event.preventDefault(); onCloseRef.current(); return; }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll(focusableSelector)].filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) { event.preventDefault(); dialogRef.current.focus(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.cancelAnimationFrame(focusInitial);
      document.body.style.overflow = previousOverflow;
      if (returnFocusTo instanceof HTMLElement && document.contains(returnFocusTo)) returnFocusTo.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="ops-modal-backdrop" onClick={onClose}>
      <div
        ref={dialogRef}
        className={`ops-modal ops-modal--${size}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="ops-modal-header">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="ops-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="ops-modal-body">{children}</div>
        {footer && <div className="ops-modal-footer">{footer}</div>}
      </div>
    </div>
  );
}
